import styled from '@emotion/styled';
import { matchRoutes, RouteObject, useLocation, Location } from 'react-router';
import { Breadcrumb as SemanticBreadcrumb, Segment, Icon, Header } from 'semantic-ui-react';

import { Crumb } from './Crumb';
import { BreadcrumbRouteMatch, BreadcrumbType } from 'types';

interface BreadcrumbProps {
  routes: Array<RouteObject>;
}

const getBreadcrumbText = (breadcrumb: BreadcrumbType | undefined, location: Location) =>
  breadcrumb
    ? typeof breadcrumb?.text === 'function'
      ? breadcrumb?.text(location)
      : breadcrumb?.text
    : undefined;

export const Breadcrumb = ({ routes }: BreadcrumbProps) => {
  const location = useLocation();
  const matchedRoutes = ((matchRoutes(routes, location) as BreadcrumbRouteMatch[]) ?? [])
    .map((x) => ({ pathname: x.pathname, breadcrumb: x.route.breadcrumb }))
    .filter((x) => x.breadcrumb?.text);

  if (!matchedRoutes || matchedRoutes.length === 0) return null;

  return (
    <S.Segment>
      {/* Full Breadcrumb implementation - could be used when whole application will be switched to new react app */}
      {/* <BreadcrumbLinks matchedRoutes={matchedRoutes} /> */}
      <BreadcrumbHeader matchedRoutes={matchedRoutes} />
    </S.Segment>
  );
};

export const BreadcrumbLinks = ({
  matchedRoutes,
}: {
  matchedRoutes: Array<{ breadcrumb: BreadcrumbType | undefined; pathname: string }>;
}) => {
  const location = useLocation();

  return (
    <SemanticBreadcrumb>
      {matchedRoutes.map(({ pathname, breadcrumb }, i) => (
        <Crumb
          key={pathname}
          inactive={breadcrumb?.inactive}
          last={i === matchedRoutes.length - 1}
          pathname={pathname}
        >
          {breadcrumb?.icon && <Icon name={breadcrumb.icon} style={{ marginRight: '0.5em' }} />}
          {getBreadcrumbText(breadcrumb, location)}
        </Crumb>
      ))}
    </SemanticBreadcrumb>
  );
};

const BreadcrumbHeader = ({
  matchedRoutes,
}: {
  matchedRoutes: Array<{ breadcrumb: BreadcrumbType | undefined; pathname: string }>;
}) => {
  const location = useLocation();
  const breadcrumb = matchedRoutes[matchedRoutes.length - 1]?.breadcrumb;

  return (
    <S.Header>
      {breadcrumb?.icon && <Icon name={breadcrumb.icon} style={{ marginRight: '0.5em' }} />}
      {getBreadcrumbText(breadcrumb, location)}
    </S.Header>
  );
};

const S = {
  Segment: styled(Segment)`
    && {
      display: flex;
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
      position: sticky;
      border-radius: 0;
      z-index: 10;
    }
  `,
  Header: styled(Header)`
    display: flex;
    align-items: center;
  `,
};
